/**
 * Estimate maths for Claim tax back.
 *
 * How Gift Aid relief works:
 * - The donor gives a net amount. The charity claims basic-rate Gift Aid on
 *   top: 25% of the net amount (a £100 gift becomes £125 gross).
 * - A donor whose marginal tax rate is above the basic 20% can personally
 *   claim the difference between their rate and the basic rate, applied to
 *   the GROSS amount. A 40% payer claims 20% of £125 = £25 per £100 given,
 *   which is 25% of the net gift - deliberately identical to the charity's
 *   Gift Aid, so the two figures must always be labelled distinctly on screen.
 *
 * All amounts are pence. Results round to the nearest penny so the breakdown
 * always sums to the headline figure a donor might quote to HMRC.
 */

import type { ClaimEstimate } from '@/types/claimTaxBack'
import type { ClaimTaxYearConfig } from './taxYearConfig'
import { findIncomeBandById } from './taxYearConfig'

/** Basic-rate Gift Aid the charity claims: net × basicRate / (100 − basicRate), i.e. 25% at 20%. */
export function giftAidOnGiven(
  givenPence: number,
  basicRatePercent: number
): number {
  return Math.round(
    (givenPence * basicRatePercent) / (100 - basicRatePercent)
  )
}

/**
 * Builds the full explainable estimate for a donor's giving at a marginal rate.
 * A basic-rate (or below) donor gets a zero claimable amount, never a negative.
 */
export function calculateClaimEstimate(
  givenPence: number,
  marginalRatePercent: number,
  config: ClaimTaxYearConfig
): ClaimEstimate {
  if (!Number.isInteger(givenPence) || givenPence < 0) {
    throw new Error(
      `Given amount must be a non-negative integer in pence, got ${givenPence}`
    )
  }

  const giftAidPence = giftAidOnGiven(givenPence, config.basicRatePercent)
  const grossPence = givenPence + giftAidPence

  // Relief is the donor's rate above basic, never below zero (Scottish starter
  // rate payers at 19% are not asked to repay the difference).
  const reliefRatePercentPoints = Math.max(
    0,
    marginalRatePercent - config.basicRatePercent
  )

  const claimablePence = Math.round(
    (grossPence * reliefRatePercentPoints) / 100
  )

  return {
    givenPence,
    giftAidPence,
    grossPence,
    marginalRatePercent,
    reliefRatePercentPoints,
    claimablePence,
  }
}

/**
 * Convenience for the checker: estimate from a selected band id.
 * Throws if the band id does not exist in the config (donor input is a fixed
 * choice list, so an unknown id is a programming error, not user error).
 */
export function calculateClaimEstimateForBand(
  givenPence: number,
  bandId: string,
  config: ClaimTaxYearConfig
): ClaimEstimate {
  const band = findIncomeBandById(config, bandId)
  if (!band) {
    throw new Error(
      `Unknown income band "${bandId}" for tax year ${config.periodKey}`
    )
  }
  return calculateClaimEstimate(givenPence, band.marginalRatePercent, config)
}

/** True when the estimate shows the donor pays above basic rate (account marker). */
export function isHighRateEstimate(estimate: ClaimEstimate): boolean {
  return estimate.reliefRatePercentPoints > 0
}
